import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Pages/PagesCss/Home.css';

const Home = () => {
  const categories = [
    {
      name: "Credit Management",
      tools: [
        { icon: "fa-solid fa-credit-card", title: "Credit Card Payoff Calculator", description: "Estimate how long it’ll take to pay off your credit card and how much interest you’ll save.", link: "/CreditCardPayoffCalculator" },
        { icon: "fa-solid fa-percent", title: "Minimum Payment Interest Calculator", description: "See how much interest you’ll pay if you only make minimum payments on your credit card.", link: "/PaymentInterestCalculator" },
        { icon: "fa-solid fa-money-bill-trend-up", title: "Credit Card Interest Calculator", description: "Calculate total interest charges and plan better repayment strategies.", link: "/CreditCardInterestCalculator" },
      ],
    },
    {
      name: "Savings",
      tools: [
        { icon: "fa-solid fa-right-left", title: "Balance Transfer Savings Calculator", description: "Find how much you can save by transferring your balance to a lower-interest card.", link: "/BalanceTransferCalculator" },
        { icon: "fa-solid fa-gift", title: "Cashback & Rewards Comparison Tool", description: "Compare credit card reward programs and see which gives the best cashback value.", link: "/CashbackRewardsCalculator" },
        { icon: "fa-solid fa-arrow-up-right-dots", title: "Credit Limit Increase Estimator", description: "Estimate your chances of getting a credit limit increase and its effect on your credit score.", link: "/CreditLimitIncreaseEstimator" },
      ],
    },
    {
      name: "Credit",
      tools: [
        { icon: "fa-solid fa-circle-notch", title: "Credit Utilization Ratio Calculator", description: "Calculate your credit utilization ratio to understand its impact on your credit score.", link: "/CreditUtilizationCalculator" },
        { icon: "fa-solid fa-scale-balanced", title: "Debt-to-Income (DTI) Ratio Calculator", description: "Find your DTI ratio and assess your financial health before applying for new credit.", link: "/DTICalculator" },
      ],
    },
    {
      name: "Planning",
      tools: [
        { icon: "fa-solid fa-thumbs-up", title: "Card Approval Odds Checker", description: "Check your approval chances for new credit cards before applying.", link: "/CardApprovalOddsChecker" },
        { icon: "fa-solid fa-rotate", title: "Credit Card Upgrade Analyzer", description: "Find out if upgrading your credit card could offer better rewards and lower fees.", link: "/CreditCardUpgradeAnalyzer" },
        { icon: "fa-solid fa-dollar-sign", title: "Annual Fee Value Calculator", description: "Determine if your card’s annual fee is worth the rewards you earn.", link: "/AnnualFeeValueCalculator" },
      ],
    },
  ];

  const testimonials = [
    { name: 'Sarah Johnson', role: 'Small Business Owner', content: 'CreditsIQ helped me understand my credit utilization and improve my score by 80 points in just 3 months!', avatar: 'SJ' },
    { name: 'Michael Chen', role: 'First-time Home Buyer', content: 'The mortgage calculator saved me thousands by helping me find the perfect loan terms for my budget.', avatar: 'MC' },
    { name: 'Emily Rodriguez', role: 'Recent Graduate', content: 'As someone new to financial planning, CreditsIQ made complex concepts easy to understand and apply.', avatar: 'ER' }
  ];

  const stats = [
    { number: '50K+', label: 'Active Users' },
    { number: '$2.5M+', label: 'Debt Managed' },
    { number: '150+', label: 'Points Improved' },
    { number: '98%', label: 'Satisfaction Rate' }
  ];

  const [activeTab, setActiveTab] = useState('All');
  const allTools = categories.flatMap(cat => cat.tools);
  const displayedTools = activeTab === 'All' ? allTools : categories.find(cat => cat.name === activeTab)?.tools || [];
  const tabs = ['All', ...categories.map(cat => cat.name)];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge"><span>Smart Financial Intelligence</span></div>
            <h1 className="hero-title">
              Master Your <span className="gradient-text">Financial Future</span> with Intelligent Credit Insights
            </h1>
            <p className="hero-description">
              CreditsIQ empowers you with advanced credit analysis, smart calculators, and personalized financial tools to make informed decisions and achieve your financial goals.
            </p>
            <div className="hero-actions">
              <Link to="/CardApprovalOddsChecker" className="btn btn-primary">Analyze Your Credit</Link>
              <Link to="/CreditCardPayoffCalculator" className="btn btn-secondary">Explore Tools</Link>
            </div>
            <div className="hero-stats">
              {stats.map((stat, index) => (
                <div key={index} className="stat-item">
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Hero Visual Cards */}
          <div className="hero-visual">
            <div className="floating-card card-1">
              <div className="card-header"><div className="card-icon"><i className="fa-solid fa-credit-card"></i></div><div className="card-title">Credit Score</div></div>
              <div className="card-score">720</div>
              <div className="card-trend">↑ 15 points</div>
            </div>
            <div className="floating-card card-2">
              <div className="card-header"><div className="card-icon"><i className="fa-solid fa-chart-line"></i></div><div className="card-title">Debt Progress</div></div>
              <div className="card-progress">
                <div className="progress-bar"><div className="progress-fill" style={{ width: '65%' }}></div></div>
                <div className="progress-text">65% Paid</div>
              </div>
            </div>
            <div className="floating-card card-3">
              <div className="card-header"><div className="card-icon"><i className="fa-solid fa-bullseye"></i></div><div className="card-title">Goal Tracking</div></div>
              <div className="card-goal">$15,000 / $20,000</div>
              <div className="card-subtitle">Emergency Fund</div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Ecosystem / Filterable Tabs */}
      <section className="features">
        <div className="container">
          <div className="section-header-1">
            <h2 className="section-title">Our Product Ecosystem</h2>
            <p className="section-description">
              Discover our suite of innovative tools designed to solve real-world problems across various domains. Each product is crafted with precision and built to deliver exceptional value.
            </p>
          </div>

          {/* Tabs */}
          <div className="tabs">
            {tabs.map((tab, i) => (
              <button
                key={i}
                className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tools Grid */}
          <div className="features-grid">
  {displayedTools.map((tool, index) => (
    <div key={index} className="feature-card">
      <div className="feature-content">
        <div className="feature-left">
          <div className="feature-icon"><i className={tool.icon}></i></div>
        </div>
        <div className="feature-right">
          <h4 className="feature-title">{tool.title}</h4>
          <p className="feature-description">{tool.description}</p>
        </div>
      </div>
      <Link to={tool.link} className="feature-link"><i class="fa-solid fa-arrow-up-right-from-square"></i> Try Now</Link>
    </div>
  ))}
</div>

        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <div className="container">
          <div className="section-header-1">
            <h2 className="section-title">How CreditsIQ Works</h2>
            <p className="section-description">Simple steps to transform your financial health</p>
          </div>
          <div className="steps">
            <div className="step"><div className="step-number">01</div><div className="step-content"><h3>Connect & Analyze</h3><p>Securely connect your financial accounts for comprehensive analysis</p></div></div>
            <div className="step"><div className="step-number">02</div><div className="step-content"><h3>Get Insights</h3><p>Receive personalized recommendations based on your financial profile</p></div></div>
            <div className="step"><div className="step-number">03</div><div className="step-content"><h3>Take Action</h3><p>Use our tools to implement strategies and track your progress</p></div></div>
            <div className="step"><div className="step-number">04</div><div className="step-content"><h3>Achieve Goals</h3><p>Watch your credit improve and reach your financial milestones</p></div></div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials">
        <div className="container">
          <div className="section-header-1">
            <h2 className="section-title">Trusted by Thousands</h2>
            <p className="section-description">See how CreditsIQ is helping people achieve financial success</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-content"><p>"{testimonial.content}"</p></div>
                <div className="testimonial-author">
                  <div className="author-avatar">{testimonial.avatar}</div>
                  <div className="author-info">
                    <div className="author-name">{testimonial.name}</div>
                    <div className="author-role">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="contact-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Start Managing Your Credit Smarter, It’s 100% Free</h2>
            <p>Join thousands of users who use our free tools to understand, plan, and improve their financial health.</p>
            <div className="cta-buttons">
              <a href="/CreditLimitIncreaseEstimator" className="btn btn-primary" rel="noopener noreferrer">
  Explore Free Tools
</a>
<a href="/About" className="btn btn-secondary">
  Learn More
</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;